import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Zap, Wifi, Bell } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallPromptProps {
  className?: string;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className = '' }) => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed/running in standalone mode
    const checkStandalone = () => {
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone ||
        document.referrer.includes('android-app://');
      
      setIsStandalone(isStandaloneMode);
      setIsInstalled(isStandaloneMode);
    };

    checkStandalone();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      const installEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(installEvent);
      
      // Show our custom install prompt after a short delay
      setTimeout(() => {
        if (!isInstalled && !isStandalone) {
          setShowPrompt(true);
        }
      }, 3000); // Show after 3 seconds
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      // console.log('PWA: App was installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, isStandalone]);

  const handleInstall = async () => {
    if (!installPrompt) {
      return;
    }

    try {
      // Show the install prompt
      await installPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        // console.log('PWA: User accepted the install prompt');
        setIsInstalled(true);
      } else {
        // console.log('PWA: User dismissed the install prompt');
      }
      
      // Clear the install prompt
      setInstallPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      // console.error('PWA: Error during installation:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed, in standalone mode, or user dismissed
  if (isInstalled || isStandalone || !installPrompt || !showPrompt) {
    return null;
  }

  // Don't show if user already dismissed in this session
  if (sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 ${className}`}>
      <div className="bg-white rounded-2xl shadow-lifted border border-primary-200 p-6 transform transition-all duration-300 ease-out">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black">
                Install ClipThis
              </h3>
              <p className="text-sm text-primary-600">
                Get the best experience
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-primary-400 hover:text-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-3 text-sm text-primary-700">
            <Zap className="w-4 h-4 text-red-500" />
            <span>Faster loading and performance</span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-primary-700">
            <Wifi className="w-4 h-4 text-red-500" />
            <span>Works offline with cached clips</span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-primary-700">
            <Bell className="w-4 h-4 text-red-500" />
            <span>Get notified when clips are ready</span>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleInstall}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Install App</span>
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-3 text-primary-600 hover:text-black transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
